import Fontfaceobserver from 'fontfaceobserver';
import { isArray, isString, uniq } from 'lodash';
import sparkMD5 from 'spark-md5';
import { getConfigAssets } from '@/i18n/assets';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { traverseTrees } from '@/utils/tree-kit';
import type { IFont, ILayerProps } from '@/version-types';
import { getBehavior } from '../behavior';

const supportFontFace = typeof FontFace != 'undefined';

const translateArrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binaryStr = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0, len = bytes.byteLength; i < len; i++) {
        binaryStr += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binaryStr);
};

export function createFontStyle(fontName: string, fontBase64: string, isWoff2 = true) {
    if (!fontBase64) return '';
    // 优先使用 woff2 格式
    return `\
      @font-face {\
        font-family: ${fontName};\
        font-display: swap;\
        src:url('${fontBase64}') ${isWoff2 ? `format('woff2')` : `format('truetype')`};\
      }\
    `;
}

export function formatFontFamilyName(fontName: string) {
    return fontName.replace(/\./g, '');
}

/**
 * 获取字体子集链接地址
 *
 * @param src - 字体源
 * @param texts - 文本
 * @param type - 字体类型
 */
export function getFontFamilySubsetUrl(src: string, texts: string, type = 'woff2') {
    return `${getConfigAssets().fontTool}?src=${encodeURIComponent(
        src,
    )}&type=${type}&chars=${encodeURIComponent(texts)}`;
}

/**
 * 获取字体文件链接地址
 *
 * @param font - 字体信息
 */
function getFontUrl(font: IFont) {
    if (!font) return '';
    return font.woff2_file_url || font.zip_url;
}

export class FontServer {
    private identity: string;
    private fontList: IFont[];
    /**
     * 字体map
     * key: 未格式化的名称
     * value: 字体信息
     */
    private fontMap: Record<string, IFont>;

    /**
     * 图层字体集合
     *
     * - key: 图层 ID
     * - value: 字体信息
     */
    private layersFontMap: Record<
        string,
        Map<
            string /** 未格式化的名称 */,
            {
                /** 格式化的名称 */
                fontName: string;
                /** 字体子集名称 */
                subsetName: string;
            }
        >
    >;

    /**
     * 字体 Base64 编码集合
     *
     * - key: 字体名称
     * - value: 字体 Base64 编码
     */
    private fontBase64Map: Record<string, string>;

    /**
     * 正在加载的字体信息
     *
     * - key: 字体名称
     * - value: 字体加载信息
     */
    private xmlLoadingMap: Record<string, { reject: any; resolve: any }[]>;

    constructor(identity: string) {
        this.identity = identity;
        this.fontList = [];
        this.fontMap = {};
        this.layersFontMap = {};
        this.fontBase64Map = {};
        this.xmlLoadingMap = {};
    }

    /** 销毁 */
    destroy() {
        this.layersFontMap = {};
    }

    /**
     * 创建字体样式
     *
     * @param font_name - 字体名称
     * @param font_base64 - 字体 Base64 编码
     * @param isWoff2 - 可选；是否是 woff2 格式；默认值为 `true`
     */
    createFontStyle(fontFamily: string, fontBase64: string, isWoff2 = true) {
        if (!fontFamily || !fontBase64) return '';
        return createFontStyle(formatFontFamilyName(fontFamily), fontBase64, isWoff2);
    }

    /**
     * 设置字体列表
     *
     * @param fontList - 字体列表
     */
    setList(fontList: IFont[]) {
        this.fontList = fontList;
        fontList.forEach((font) => {
            this.fontMap[font.font_name] = font;
        });
    }

    /**
     * 获取字体列表
     */
    getList() {
        return this.fontList;
    }

    /**
     * 获取字体信息
     *
     * @param font_name - 字体名称
     */
    getFont(fontFamily: string) {
        return this.fontMap[fontFamily];
    }

    /**
     * 获取图层字体对应的 Base64 编码, 用于下载
     *
     * @param layerId - 图层 ID
     * @returns [fontFamily]: [base64]
     */
    getLayerFontBase64(layer: ILayerProps) {
        const res: Record<string, string> = {};
        const layerFontInfo = this.layersFontMap[layer.id];
        if (!layerFontInfo) return res;
        for (const [fontFamily, fontInfo] of layerFontInfo.entries()) {
            res[fontFamily] = this.fontBase64Map[fontInfo.subsetName] || this.fontBase64Map[fontInfo.fontName] || '';
        }
        return res;
    }

    /**
     * 获取字体子集 Base64 编码
     *
     * @param subset_name - 字体子集名称
     */
    getSubsetFontBase64(subsetName: string) {
        return this.fontBase64Map[subsetName] || '';
    }

    /**
     * 获取图层字体信息, 用于渲染
     *
     * @param layer - 图层数据
     * @returns [fontFamily]: [用于展示的fontFamily]
     */
    getLayerFontFamily(layer: ILayerProps) {
        const res: Record<string, string> = {};
        const layerFontInfo = this.layersFontMap[layer.id];
        if (!layerFontInfo) return res;
        for (const [fontFamily, fontInfo] of layerFontInfo.entries()) {
            res[fontFamily] = fontInfo.fontName || fontInfo.subsetName || '';
        }
        return res;
    }

    /**
     * 创建图层字体子集
     *
     * @param layers - 图层数据
     * @returns 子集图层 id
     */
    async createLayersFontSubset(layers: ILayerProps[], refreshLayer = true): Promise<ILayerProps[]> {
        const layersTextsInfo: Record<
            string /** 未格式化的名称 */,
            {
                texts: string;
                textsMd5: string;
            }
        > = {};
        const layersFontMap: Record<
            string,
            Map<
                string /** 未格式化的名称 */,
                {
                    /** 格式化的名称 */
                    fontName: string;
                    /** 字体子集名称 */
                    subsetName: string;
                }
            >
        > = {};
        const hasFontLayers: Record<string, ILayerProps> = {};
        traverseTrees(layers, (layer: ILayerProps) => {
            const infos = getBehavior(this.identity, layer)?.getTextInfo?.(layer);
            if (!isArray(infos) || !infos.length) return;
            hasFontLayers[layer.id] = layer;
            layersFontMap[layer.id] = new Map();
            for (const info of infos) {
                const fontFamily = info.fontFamily;
                if (this.fontMap[fontFamily]) {
                    layersTextsInfo[fontFamily] = {
                        texts: `${layersTextsInfo[fontFamily]?.texts || ''}${info.text}`,
                        textsMd5: '',
                    };
                    const fontName = formatFontFamilyName(fontFamily);
                    layersFontMap[layer.id].set(fontFamily, {
                        fontName: this.fontBase64Map[fontName] ? fontName : '',
                        subsetName: '',
                    });
                }
            }
        });
        Object.keys(layersTextsInfo).forEach((fontFamily) => {
            const texts = [...new Set(layersTextsInfo[fontFamily].texts)].join('');
            layersTextsInfo[fontFamily].texts = texts;
            if (texts) {
                layersTextsInfo[fontFamily].textsMd5 = sparkMD5.hash(texts);
            }
        });
        const loadList: Promise<{ isWoff2: boolean }>[] = [];
        Object.keys(layersFontMap).forEach((layerId) => {
            const fontInfos = layersFontMap[layerId];
            for (const [fontFamily, fontInfo] of fontInfos.entries()) {
                if (layersTextsInfo[fontFamily].texts) {
                    const textsMd5 = layersTextsInfo[fontFamily].textsMd5;
                    const fontName = formatFontFamilyName(fontFamily);
                    fontInfo.subsetName = `${fontName}-${textsMd5}`;
                    const zipUrl = getFontFamilySubsetUrl(
                        getFontUrl(this.fontMap[fontFamily]),
                        layersTextsInfo[fontFamily].texts,
                    );
                    loadList.push(this.loadFont(fontInfo.subsetName, zipUrl));
                }
            }
        });

        if (loadList.length) {
            try {
                await Promise.all(loadList);
            } catch (e) {
                console.log('createLayersFontSubset error', e);
            }
        }

        this.layersFontMap = {
            ...this.layersFontMap,
            ...layersFontMap,
        };

        if (refreshLayer) {
            Object.keys(hasFontLayers).forEach((layerId) => {
                const layer = hasFontLayers[layerId];
                const __fontFamily = this.getLayerFontFamily(layer);
                if (__fontFamily) {
                    setLayerRuntimeValue(layer, {
                        fontFamilyMap: __fontFamily,
                    });
                }
            });
        }

        return Object.values(hasFontLayers);
    }

    /**
     * 创建文本字体子集
     *
     * @param texts - 文本
     * @param font_name - 字体名称
     * @returns 子集名称
     */
    async createTextFontSubset(texts: string, fontFamily: string) {
        const font = this.fontMap[fontFamily];
        if (!font) return null;
        const fontName = formatFontFamilyName(fontFamily);
        texts = [...new Set(texts)].join('');
        const subsetName = `${fontName}-${sparkMD5.hash(texts)}`;
        if (this.fontBase64Map[subsetName]) return subsetName;
        const zipUrl = getFontFamilySubsetUrl(getFontUrl(font), texts);
        try {
            await this.loadFont(subsetName, zipUrl);
        } catch (e) {
            console.log('createTextFontSubset error', e);
        }
        return subsetName;
    }

    /**
     * 注册图层全量字体
     *
     * @param layer - 图层数据
     * @param font_name - 字体名称
     */
    async registerFont(layer: ILayerProps, fontFamily: string): Promise<ILayerProps> {
        const infos = getBehavior(this.identity, layer)?.getTextInfo?.(layer);

        if (!isArray(infos)) return layer;
        if (!this.fontMap[fontFamily]) return layer;

        const loadList: Promise<{ isWoff2: boolean }>[] = [];

        const fontName = formatFontFamilyName(fontFamily);
        const base64 = this.fontBase64Map[fontName];
        if (!base64) {
            loadList.push(this.loadFont(fontName, getFontUrl(this.fontMap[fontFamily])));
        }

        if (loadList.length) {
            try {
                await Promise.all(loadList);
            } catch (e) {
                console.log('registerFont error', e);
            }
        }

        const fontMap =
            this.layersFontMap[layer.id] ||
            new Map<
                string,
                {
                    fontName: string;
                    subsetName: string;
                }
            >();

        fontMap.set(fontFamily, {
            fontName,
            subsetName: fontMap.get(fontFamily)?.subsetName || '',
        });

        this.layersFontMap[layer.id] = fontMap;

        setLayerRuntimeValue(layer, {
            fontFamilyMap: this.getLayerFontFamily(layer),
        });
        return layer;
    }

    /**
     * 获取正在使用的字体列表
     *
     * @param layers - 图层数据
     */
    getUsingFontList(layers: ILayerProps[]) {
        const usingFontList: IFont[] = [];
        //收集字体
        traverseTrees(layers, (layer: ILayerProps) => {
            const infos = getBehavior(this.identity, layer)?.getTextInfo?.(layer);
            if (infos) {
                for (const info of infos) {
                    const font = this.fontMap[info.fontFamily];
                    if (font) {
                        usingFontList.push(font);
                    }
                }
            }
        });

        return uniq(usingFontList);
    }

    loadFont(fontFamily: string, zipUrl: string): Promise<{ isWoff2: boolean }> {
        return new Promise((resolve, reject) => {
            if (!zipUrl) return reject({ error: 'zipUrl is empty' });
            const fontName = formatFontFamilyName(fontFamily);
            const isWoff2 = true; // 目前固定使用woff2
            const font_base64 = this.fontBase64Map[fontName];
            if (font_base64) {
                return resolve({ isWoff2 });
            }
            const xmlLoadingInfo = this.xmlLoadingMap[fontName];
            if (xmlLoadingInfo) {
                xmlLoadingInfo.push({ reject, resolve });
                return;
            }
            this.xmlLoadingMap[fontName] = [{ reject, resolve }];
            const xmlHttpRequest = new XMLHttpRequest();
            xmlHttpRequest.open('GET', zipUrl, true);
            xmlHttpRequest.responseType = 'blob';
            xmlHttpRequest.onerror = (e: any) => {
                console.log('xmlHttpRequest.onerror', e);
                const xmlLoadingInfo = this.xmlLoadingMap[fontName];
                delete this.xmlLoadingMap[fontName];
                xmlLoadingInfo.forEach(({ reject }) => reject(e));
            };
            const xmlLoadingMap = this.xmlLoadingMap;
            const fontBase64Map = this.fontBase64Map;
            xmlHttpRequest.onload = function () {
                console.log('xmlHttpRequest.onload');
                if (this.status === 200 || this.status === 206 || this.status === 304) {
                    const fileReader = new FileReader();
                    fileReader.addEventListener('load', () => {
                        const fileData = fileReader.result;
                        const xmlLoadingInfo = xmlLoadingMap[fontName];
                        delete xmlLoadingMap[fontName];
                        if (!fileData) {
                            const error = { error: 'fileReader error' };
                            xmlLoadingInfo.forEach(({ reject }) => reject(error));
                            return;
                        }
                        if (!isString(fileData)) {
                            const font = new FontFace(fontName, fileData, {
                                display: 'swap',
                            });
                            (document.fonts as any).add(font);
                            fontBase64Map[fontName] = `data:binary/octet-stream;base64,${translateArrayBufferToBase64(
                                fileData as ArrayBuffer,
                            )}`;
                            xmlLoadingInfo.forEach(({ resolve }) => resolve({ isWoff2 }));
                        } else {
                            const style = document.createElement('style');
                            const fontFaceObserver = new Fontfaceobserver(fontName);
                            style.setAttribute('data-font-name', fontName);
                            style.setAttribute('id', fontName);
                            style.innerHTML = createFontStyle(fontName, fileData, isWoff2);
                            document.getElementsByTagName('head')[0].appendChild(style);
                            fontBase64Map[fontName] = fileData;
                            fontFaceObserver
                                .load(undefined, 60 * 60 * 1000)
                                .then(() => {
                                    xmlLoadingInfo.forEach(({ resolve }) => resolve({ isWoff2 }));
                                })
                                .catch((e) => {
                                    xmlLoadingInfo.forEach(({ reject }) => reject(e));
                                });
                        }
                    });
                    fileReader.addEventListener('error', (e: any) => {
                        const xmlLoadingInfo = xmlLoadingMap[fontName];
                        delete xmlLoadingMap[fontName];
                        xmlLoadingInfo.forEach(({ reject }) => reject(e));
                    });
                    if (supportFontFace) {
                        fileReader.readAsArrayBuffer(this.response);
                    } else {
                        fileReader.readAsDataURL(this.response);
                    }
                    return;
                }
                const xmlLoadingInfo = xmlLoadingMap[fontName];
                delete xmlLoadingMap[fontName];
                const error = { error: 'xmlHttpRequest error' };
                xmlLoadingInfo.forEach(({ reject }) => reject(error));
            };
            xmlHttpRequest.send();
        });
    }
}
